import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

function parseDate(dateString) {
    if (!dateString) return null;

    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;

    // If it's not a string, return null
    if (typeof dateString !== 'string') {
        return null;
    }

    try {
        // Handle YYYY-MM-DD format properly
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);

        // Validate the parsed values
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return null;
        }

        // Create date in local timezone to avoid timezone conversion issues
        const result = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid DST issues
        return result;
    } catch (error) {
        return null;
    }
}

function daysBetween(date1, date2) {
    const d1 = parseDate(date1);
    const d2 = parseDate(date2);
    if (!d1 || !d2) return 0;
    return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
}

function isWithinDays(date, days) {
    if (!date) return false;
    const now = new Date();
    const d = parseDate(date);
    if (!d) return false;
    return d >= now && daysBetween(now, d) <= days;
}

function isExpired(date) {
    if (!date) return false;
    const now = new Date();
    const d = parseDate(date);
    if (!d) return false;
    return d < now;
}

function getDaysUntil(date) {
    if (!date) return null;
    const now = new Date();
    const d = parseDate(date);
    if (!d) return null;
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

function daysSince(date) {
    if (!date) return null;
    const now = new Date();
    const d = parseDate(date);
    if (!d) return null;
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

export default function FleetDashboard({ vehicles }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const now = new Date();
    const motDueSoon = vehicles.filter(v => isWithinDays(v.nextMot, 60)); // 2 months
    const motExpired = vehicles.filter(v => isExpired(v.nextMot));
    const insuranceExpiring = vehicles.filter(v => isWithinDays(v.insuredExpire, 60)); // 2 months
    const insuranceExpired = vehicles.filter(v => isExpired(v.insuredExpire));
    const taxExpired = vehicles.filter(v => (v.taxStatus || '').toLowerCase().includes('expired'));
    const noRecentMileage = vehicles.filter(v => !v.mileageDate || daysSince(v.mileageDate) > 7);

    // Alerts: combine all unique vehicles with issues
    const alertVehicles = [
        ...motExpired.map(v => ({ ...v, alert: 'MOT expired', days: null, severity: 'critical' })),
        ...motDueSoon.map(v => ({ ...v, alert: 'MOT due soon', days: getDaysUntil(v.nextMot), severity: 'warning' })),
        ...insuranceExpired.map(v => ({ ...v, alert: 'Insurance expired', days: null, severity: 'critical' })),
        ...insuranceExpiring.map(v => ({ ...v, alert: 'Insurance expiring', days: getDaysUntil(v.insuredExpire), severity: 'warning' })),
        ...taxExpired.map(v => ({ ...v, alert: 'Tax expired', days: null, severity: 'critical' })),
        ...noRecentMileage.map(v => ({ ...v, alert: 'No recent mileage data', days: daysSince(v.mileageDate), severity: 'warning' })),
    ];
    // Remove duplicates (by id + alert)
    const seen = new Set();
    const uniqueAlerts = alertVehicles.filter(v => {
        const key = v.id + v.alert;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    const getAlertStyle = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-50 border-l-4 border-red-400';
            case 'warning': return 'bg-yellow-50 border-l-4 border-yellow-400';
            case 'info': return 'bg-blue-50 border-l-4 border-blue-400';
            default: return 'bg-gray-50 border-l-4 border-gray-400';
        }
    };

    const getAlertIcon = (severity) => {
        switch (severity) {
            case 'critical': return '🚨';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            default: return '📋';
        }
    };

    return (
        <div className="mb-8">
            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center">
                    <span className="text-2xl font-bold text-gray-800">{vehicles.length}</span>
                    <span className="text-gray-500">Total Vehicles</span>
                </div>
                <div className="bg-yellow-100 rounded-lg shadow p-4 flex flex-col items-center">
                    <span className="text-2xl font-bold text-yellow-800">{motDueSoon.length}</span>
                    <span className="text-yellow-700">MOT Due Soon</span>
                </div>
                <div className="bg-orange-100 rounded-lg shadow p-4 flex flex-col items-center">
                    <span className="text-2xl font-bold text-orange-800">{insuranceExpiring.length}</span>
                    <span className="text-orange-700">Insurance Expiring</span>
                </div>
                <div className="bg-red-100 rounded-lg shadow p-4 flex flex-col items-center">
                    <span className="text-2xl font-bold text-red-800">{taxExpired.length}</span>
                    <span className="text-red-700">Tax Expired</span>
                </div>
                <div className="bg-blue-100 rounded-lg shadow p-4 flex flex-col items-center">
                    <span className="text-2xl font-bold text-blue-800">{noRecentMileage.length}</span>
                    <span className="text-blue-700">No Recent Mileage</span>
                </div>
            </div>
            {/* Alerts - Collapsible */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
                <div
                    className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <h3 className="text-lg font-semibold text-gray-900">Vehicle Alerts</h3>
                            <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                {uniqueAlerts.length} {uniqueAlerts.length === 1 ? 'alert' : 'alerts'}
                            </span>
                        </div>
                    </div>
                </div>
                {isExpanded && (
                    <div className="p-4">
                        {uniqueAlerts.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No critical issues 🎉</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Alert</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Vehicle</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Details</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {uniqueAlerts.map(v => (
                                            <tr key={v.id + v.alert} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 text-sm">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        v.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {getAlertIcon(v.severity)} {v.alert}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                    {v.make} {v.model} ({v.registration})
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    {v.nextMot && v.alert.toLowerCase().includes('mot') && (
                                                        <span>MOT: {(v.nextMot || '').split('T')[0]}</span>
                                                    )}
                                                    {v.insuredExpire && v.alert.toLowerCase().includes('insurance') && (
                                                        <span>Insurance: {(v.insuredExpire || '').split('T')[0]}</span>
                                                    )}
                                                    {v.mileageDate && v.alert.toLowerCase().includes('mileage') && (
                                                        <span>Last Mileage: {(v.mileageDate || '').split('T')[0]}</span>
                                                    )}
                                                    {v.days !== null && v.alert === 'No recent mileage data' && (
                                                        <span>{v.days} days since last mileage</span>
                                                    )}
                                                    {v.days && v.alert !== 'No recent mileage data' && (
                                                        <span>{v.days} days until expiry</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <Link 
                                                        to={`/fleet/vehicles/${v.id}`} 
                                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                                    >
                                                        View Vehicle
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
} 